import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { isMobile, isTablet } from '../../utils/deviceUtils';

const faqCategories = [
  {
    id: 'booking',
    title: 'Booking & Reservations',
    icon: '📅',
  },
  {
    id: 'safari',
    title: 'Safari Experience',
    icon: '🦁',
  },
  {
    id: 'transport',
    title: 'Transport & Logistics',
    icon: '🚗',
  },
  {
    id: 'payment',
    title: 'Payment & Pricing',
    icon: '💳',
  },
  {
    id: 'safety',
    title: 'Safety & Health',
    icon: '🛡️',
  },
  {
    id: 'platform',
    title: 'Platform & Account',
    icon: '💻',
  },
];

const faqData = {
  booking: [
    {
      question: 'How far in advance should I book my safari?',
      answer:
        'We recommend booking your safari at least 3-6 months in advance, especially during peak season (June-October). For popular destinations like the Serengeti during the Great Migration, booking 6-12 months ahead is advisable to secure the best accommodations and guides.',
    },
    {
      question: 'Can I modify or cancel my booking?',
      answer:
        'Yes, you can modify or cancel your booking through your account dashboard. Cancellation policies vary by operator and package. Most bookings can be cancelled up to 30 days before departure with a full refund, while cancellations within 30 days may incur fees. Check your specific booking terms for details.',
    },
    {
      question: 'What happens if my safari is cancelled by the operator?',
      answer:
        "If an operator cancels your safari, you'll receive a full refund or the option to reschedule. We'll notify you immediately and help you find alternative dates or packages. Our customer support team will assist you throughout the process.",
    },
    {
      question: 'Do I need travel insurance?',
      answer:
        'While not mandatory, we strongly recommend comprehensive travel insurance that covers medical emergencies, trip cancellation, and evacuation. Safari activities involve wildlife encounters and remote locations where medical facilities may be limited.',
    },
  ],
  safari: [
    {
      question: 'What should I pack for my safari?',
      answer:
        'Essential items include neutral-colored clothing (khaki, beige, green), comfortable walking shoes, a wide-brimmed hat, sunglasses, sunscreen, insect repellent, binoculars, camera with extra batteries, and any personal medications. Avoid bright colors and strong perfumes as they can attract wildlife.',
    },
    {
      question: 'What wildlife can I expect to see?',
      answer:
        'Tanzania is home to the Big Five (lion, leopard, elephant, buffalo, rhino) plus cheetahs, giraffes, zebras, wildebeest, hippos, crocodiles, and hundreds of bird species. Sightings vary by season and location. The Great Migration in Serengeti offers spectacular wildlife concentrations.',
    },
    {
      question: 'Are safaris safe?',
      answer:
        "Yes, safaris are very safe when conducted by licensed operators. Our guides are experienced professionals who follow strict safety protocols. You'll receive safety briefings before each activity. Always follow your guide's instructions and stay in designated areas.",
    },
    {
      question: "What's the best time of year for a safari?",
      answer:
        'The dry season (June-October) offers the best wildlife viewing as animals gather around water sources. The wet season (November-May) brings lush landscapes and fewer crowds. The Great Migration typically occurs from July to October in the Serengeti.',
    },
  ],
  transport: [
    {
      question: 'How do I get to my safari destination?',
      answer:
        'Most safaris start from major cities like Arusha or Dar es Salaam. We can arrange airport transfers and domestic flights. Many packages include transportation from your arrival point to the safari starting location. Contact us for specific arrangements.',
    },
    {
      question: 'What type of vehicles are used for safaris?',
      answer:
        'Safaris typically use 4x4 Land Cruisers or similar vehicles with pop-up roofs for optimal wildlife viewing. These vehicles are specially equipped for off-road conditions and provide comfortable seating for 6-8 passengers.',
    },
    {
      question: 'Can I book transport separately from my safari?',
      answer:
        'Yes, we offer standalone transport services between destinations. Our transport operators provide reliable, comfortable vehicles with experienced drivers. You can book transport for airport transfers, inter-city travel, or custom routes.',
    },
    {
      question: 'What if I have mobility limitations?',
      answer:
        "Many safari vehicles and lodges can accommodate guests with mobility limitations. Please inform us of any special requirements when booking, and we'll work with operators to ensure your comfort and accessibility.",
    },
  ],
  payment: [
    {
      question: 'What payment methods do you accept?',
      answer:
        'We accept major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers. Payment plans are available for many packages. All payments are processed securely through our encrypted payment system.',
    },
    {
      question: 'Are there hidden fees?',
      answer:
        'No hidden fees. All costs are clearly displayed before booking, including park fees, accommodation, meals, and activities. Optional extras like alcoholic beverages, tips, and personal expenses are clearly marked.',
    },
    {
      question: 'Can I pay in installments?',
      answer:
        'Yes, many packages offer payment plans. Typically, a 25-30% deposit is required to confirm your booking, with the balance due 30-60 days before departure. Contact us for specific payment plan options.',
    },
    {
      question: "What's included in the safari price?",
      answer:
        'Safari prices typically include accommodation, meals, game drives, park fees, guide services, and transportation during the safari. Exclusions usually include international flights, visas, travel insurance, tips, and personal expenses.',
    },
  ],
  safety: [
    {
      question: 'What health precautions should I take?',
      answer:
        'Consult your doctor about vaccinations (yellow fever, hepatitis A/B, typhoid). Malaria prophylaxis is recommended. Bring insect repellent and use mosquito nets. Stay hydrated and protect yourself from the sun. Carry a basic first-aid kit.',
    },
    {
      question: 'Is it safe to travel to Tanzania?',
      answer:
        'Tanzania is generally safe for tourists. Major safari destinations are well-established and secure. Follow standard travel precautions: keep valuables secure, avoid isolated areas at night, and follow local advice. Our operators prioritize guest safety.',
    },
    {
      question: 'What emergency support is available?',
      answer:
        'All our operators provide 24/7 emergency support. Guides carry satellite phones and first-aid kits. Major lodges have medical facilities or access to nearby clinics. We maintain emergency contact numbers for all destinations.',
    },
    {
      question: 'How do you handle medical emergencies?',
      answer:
        'In case of medical emergencies, guides are trained in first aid and can contact emergency services. Evacuation services are available for serious cases. We recommend comprehensive travel insurance that includes medical evacuation coverage.',
    },
  ],
  platform: [
    {
      question: 'How do I create an account?',
      answer:
        "Click 'Register' in the top navigation and choose your user type (traveler, tour operator, or transporter). Fill in your details and verify your email. You can also sign up with Google for travelers.",
    },
    {
      question: 'How do I become a tour operator?',
      answer:
        'Register as a tour operator and provide required documentation including business license, insurance certificates, and guide certifications. Our team will review your application and verify your credentials before approval.',
    },
    {
      question: 'Can I save trips for later?',
      answer:
        'Yes, you can save trips to your wishlist by clicking the heart icon. Access your saved trips from your account dashboard. You can also create custom alerts for price drops or availability.',
    },
    {
      question: 'How do I contact customer support?',
      answer:
        'Contact us through the contact form, email (support@dalarides.com), or phone (+255 123 456 789). We provide 24/7 support for urgent matters and typically respond to general inquiries within 24 hours.',
    },
  ],
};

export const FAQ: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('booking');
  const [openQuestions, setOpenQuestions] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const mobile = isMobile();
  const tablet = isTablet();

  const toggleQuestion = (index: number) => {
    setOpenQuestions(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const filteredFAQs = faqData[activeCategory as keyof typeof faqData].filter(
    faq =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Hero Section */}
      <section className="h-auto bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 p-4 sm:p-6 lg:p-8">
          <div className="text-center pt-28 pb-8">
            <h1
              className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-black mb-4`}
            >
              Frequently Asked Questions
            </h1>
            <p
              className={`${mobile ? 'text-base' : 'text-lg'} text-white/90 max-w-2xl mx-auto`}
            >
              Find answers to common questions about safaris, bookings, and our
              platform. Can't find what you're looking for? Contact our support
              team.
            </p>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="bg-surface-primary border-b border-border-primary py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-border-primary bg-background-primary text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section
        className={`${mobile ? 'py-16' : tablet ? 'py-16' : 'py-20'} bg-background-primary`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`grid ${mobile ? 'grid-cols-1 gap-8' : tablet ? 'grid-cols-1 lg:grid-cols-4 gap-8' : 'grid-cols-1 lg:grid-cols-4 gap-12'}`}
          >
            {/* Category Navigation */}
            <div className="lg:col-span-1">
              <h2
                className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-6`}
              >
                Categories
              </h2>
              <div className="space-y-2">
                {faqCategories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full text-left p-4 rounded-xl transition-colors ${
                      activeCategory === category.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-primary text-text-secondary hover:bg-background-secondary'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{category.icon}</span>
                      <span className="font-medium">{category.title}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* FAQ Questions */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-soft p-6 sm:p-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-6`}
                >
                  {faqCategories.find(cat => cat.id === activeCategory)?.title}
                </h2>

                {filteredFAQs.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-text-secondary">
                      No questions found matching your search.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredFAQs.map((faq, index) => (
                      <div
                        key={index}
                        className="border border-border-primary rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleQuestion(index)}
                          className="w-full text-left p-4 sm:p-6 hover:bg-background-secondary transition-colors flex items-center justify-between"
                        >
                          <h3
                            className={`${mobile ? 'text-base' : 'text-lg'} font-semibold text-text-primary pr-4`}
                          >
                            {faq.question}
                          </h3>
                          {openQuestions.includes(index) ? (
                            <ChevronUp className="w-5 h-5 text-text-tertiary flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-text-tertiary flex-shrink-0" />
                          )}
                        </button>
                        {openQuestions.includes(index) && (
                          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                            <p className="text-text-secondary leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Contact Support */}
              <div className="mt-8 bg-gradient-to-r from-primary-50 to-accent-coral-50 rounded-xl p-6 sm:p-8 border border-primary-100">
                <h3
                  className={`${mobile ? 'text-lg' : 'text-xl'} font-bold text-text-primary mb-3`}
                >
                  Still have questions?
                </h3>
                <p className="text-text-secondary mb-4">
                  Can't find the answer you're looking for? Our support team is
                  here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/contact"
                    className="bg-primary-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-primary-600 transition-colors text-center"
                  >
                    Contact Support
                  </a>
                  <a
                    href="mailto:support@dalarides.com"
                    className="bg-white text-primary-500 border border-primary-500 px-6 py-3 rounded-xl font-medium hover:bg-primary-50 transition-colors text-center"
                  >
                    Email Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
